import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import download from 'downloadjs';
import { FiSearch, FiX } from 'react-icons/fi';
import { BsFillInfoCircleFill } from 'react-icons/all';
import { Button, Heading, Input, Textarea } from '~/components';
import { Stepper, Steps } from '~/components/Stepper';
import { ContractCard } from '~/pages/Portal/TerminationRequests/ContractCard';
import { SelectEquipmentCard } from '~/pages/Portal/TerminationRequests/SelectEquipmentCard';
import { RequestProposal } from '~/pages/Portal/TerminationRequests/RequestProposal';
import { useExternoCan, useQueryParams } from '~/hooks';
import {
  RequestProposalObj,
  TerminationRequestsContractView,
  TerminationRequestsEquipament,
  UpdateCalculation,
} from '~/types/terminationRequests';
import {
  downloadTerminationDoc,
  downloadTerminationPdf,
  getContractDetailsByNumber,
  getTerminationById,
  getTerminationSimulationId,
  postCancelProposal,
  postRequestProposal,
  postUpdateCalculation,
  putRequestProposal,
} from '~/services/TerminationRequests';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { TotalValuesContract } from '~/pages/Portal/TerminationRequests/TotalValuesContract';
import { formatCurrency } from '~/utils/formatCurrency';
import { formataNumberStrToFloat } from '~/utils/formatString';
import { TerminationAgreement } from '~/pages/Portal/TerminationRequests/TerminationAgreement';

export const RequestContractTermination: React.VFC = () => {
  const queryParams = useQueryParams();
  const toast = useToast();
  const history = useHistory();
  const isVisualization = queryParams.get('visualization') === 'true';
  const isVisualizationAnalysis =
    queryParams.get('visualization-analysis') === 'true';
  const isAnalysis = queryParams.get('analysis') === 'true';
  const isSimulation = queryParams.get('simulation') === 'true';
  const contractIds = queryParams.get('contractIds')?.split(',') ?? [];
  const terminationId = queryParams.get('terminationId') ?? '';
  const isClient = useExternoCan({ roles: ['cliente'] });
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [contratos, setContratos] = useState<TerminationRequestsContractView[]>(
    [],
  );
  const [objProposta, setObjProposta] = useState<
    RequestProposalObj | undefined
  >(undefined);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [erro, setErro] = useState('');
  const [motivo, setMotivo] = useState<string>('');
  const [status, setStatus] = useState<number>(0);
  const [descricao, setDescricao] = useState('');
  const [grupoeconomico, setGrupoeconomico] = useState('');
  const [updateCalculationObj, setUpdateCalculationObj] = useState<
    UpdateCalculation | undefined
  >(undefined);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [arquivo, setSelectedFile] = useState<any>();
  const [nomeArquivo, setNomeArquivo] = useState<string | undefined>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [idTerminationSimulation, setIdTerminationSimulation] = useState('');
  const [solicitante, setSolicitante] = useState(0);
  useEffect(() => {
    load();
  }, [isVisualization, isAnalysis, isSimulation]);

  const load = () => {
    setLoading(true);
    if (
      isAnalysis ||
      isVisualization ||
      isVisualizationAnalysis ||
      isSimulation
    ) {
      if (!isSimulation) {
        getTerminationByIdFn();
      } else {
        getTerminationSimulationId(contractIds)
          .then(res => {
            getTerminationByIdFn(res);
            setIdTerminationSimulation(res);
          })
          .catch(err => {
            setErro(err.response.data.message);
            onOpen();
          });
      }
    } else {
      getContractDetailsByNumber(contractIds)
        .then(r => {
          setContratos(r);
        })
        .catch(err => {
          if (!isVisualization && !isAnalysis && !isVisualizationAnalysis) {
            setErro(err.response.data.message);
            onOpen();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const getTerminationByIdFn = (idSimulation?: string) => {
    getTerminationById(
      idSimulation || terminationId,
      isAnalysis,
      isVisualizationAnalysis,
      isSimulation,
    )
      .then(r => {
        setContratos(r.contratos);
        setStatus(r.status);
        setMotivo(r.motivo);
        setDescricao(r.descricao_motivo);
        setGrupoeconomico(r.grupoeconomico);
        setSolicitante(r.solicitante);
        if (isAnalysis || isVisualizationAnalysis || isSimulation) {
          setarObjetoProposta(r.motivo.toString(), r.descricao_motivo);
          setUpdateCalculationObj({
            rescisao: Number(terminationId),
            desconto: r.desconto,
            valor_total: r.valor_total,
            valor_total_geral: r.valor_total_geral,
            contratos: r.contratos.map(it => {
              return {
                contrato: it.id_contrato,
                aviso_previo: it.aviso_previo,
                limite_dias: it.limite_dias,
                valor_mensal_atual: Number(
                  it.valor_mensal_atual
                    .replace('R$', '')
                    .replace('.', '')
                    .replace(',', '.'),
                ),
                multa: it.multa,
                valor_multa: Number(
                  it.valor_multa
                    .replace('R$', '')
                    .replace('.', '')
                    .replace(',', '.'),
                ),
                valor_total: Number(
                  it.valor_total
                    .replace('R$', '')
                    .replace('.', '')
                    .replace(',', '.'),
                ),
              };
            }),
          });
          verificaContratosInvalidos(r.contratos);
        }
      })
      .catch(err => {
        if (!isVisualization && !isVisualizationAnalysis) {
          if (err.response?.status === 412) {
            setErro(err.response.data.message);
            onOpen();
          } else if (err.response?.status === 400 && isAnalysis) {
            setErro(err.response.data.detail);
            onOpen();
          } else {
            toast({
              title: 'Erro',
              description: 'Erro ao carregar contratos',
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const steps =
    !isAnalysis && !isVisualization && !isVisualizationAnalysis && !isSimulation
      ? [
          { id: 1, name: 'Contratos', selected: true, disabled: false },
          {
            id: 2,
            name: 'Selecionar equipamentos',
            selected: false,
            disabled: !isVisualization && !isAnalysis,
          },
          {
            id: 3,
            name: 'Solicitar proposta',
            selected: false,
            disabled: !isVisualization && !isAnalysis,
          },
        ]
      : !isAnalysis && isVisualization && !isVisualizationAnalysis
      ? [
          { id: 1, name: 'Contratos', selected: true, disabled: false },
          {
            id: 2,
            name: 'Equipamentos',
            selected: false,
            disabled: !isVisualization && !isAnalysis,
          },
          {
            id: 3,
            name: !isVisualization ? 'Motivo do Cliente' : 'Motivo da Rescisão',
            selected: false,
            disabled: !isVisualization && !isAnalysis,
          },
        ]
      : isSimulation
      ? [
          { id: 1, name: 'Simulação', selected: true, disabled: false },
          {
            id: 2,
            name: 'Termo de Rescisão',
            selected: false,
            disabled: false,
          },
        ]
      : [
          { id: 1, name: 'Contratos', selected: true, disabled: false },
          {
            id: 2,
            name: 'Equipamentos',
            selected: false,
            disabled:
              !isVisualization && !isAnalysis && !isVisualizationAnalysis,
          },
          {
            id: 3,
            name:
              !isVisualization && !isVisualizationAnalysis
                ? 'Motivo do Cliente'
                : 'Motivo da Rescisão',
            selected: false,
            disabled:
              !isVisualization && !isAnalysis && !isVisualizationAnalysis,
          },
          {
            id: 4,
            name: 'Termo de Rescisão',
            selected: false,
            disabled:
              !isVisualization && !isAnalysis && !isVisualizationAnalysis,
          },
        ];
  const [stepsControl, setSteps] = useState(steps);
  const [stepSelecionado, setStepSelecionado] = useState<Steps>(steps[0]);
  const [loading, setLoading] = useState(false);
  const {
    isOpen: isOpenModalCancel,
    onOpen: onOpenModalCancel,
    onClose: onCloseModalCancel,
  } = useDisclosure();
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const { formState } = useForm();
  const { errors } = formState;

  const solicitarProposta = (ignorarModal = false) => {
    if (!ignorarModal) {
      onOpenModal();
      return;
    }
    setLoading(true);
    if (isAnalysis || status === 2 || isSimulation) {
      putRequestProposal(
        isSimulation ? idTerminationSimulation : terminationId,
        objProposta!,
        updateCalculationObj!,
        contratos,
        arquivo,
        isSimulation,
      )
        .then(() => {
          setLoading(false);
          toast({
            title: isSimulation
              ? 'Proposta enviada com sucesso'
              : status === 2
              ? 'Proposta assinada com sucesso'
              : 'Termo de rescisão enviado com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          history.goBack();
        })
        .catch(err => {
          setLoading(false);
          if (status === 2 || isAnalysis) {
            const errorsValidate = Object.entries(err.response.data);
            errorsValidate.forEach(_err => {
              toast({
                title: 'Erro ao enviar proposta',
                description: String(_err[1]),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
              });
            });
          } else {
            toast({
              title: 'Erro ao enviar proposta',
              description: err.response.data,
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        });
      return;
    }
    postRequestProposal(objProposta!)
      .then(() => {
        setLoading(false);
        toast({
          title: 'Rescisão solicitada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      })
      .catch(err => {
        setLoading(false);
        toast({
          title: 'Erro ao solicitar Rescisão',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const removerContrato = (contract: TerminationRequestsContractView) => {
    const contratos_filtrados = contratos.filter(
      it => it.nu_contrato !== contract.nu_contrato,
    );
    setContratos(contratos_filtrados);

    if (contratos.length === 1) {
      setSteps(stepsControl.map(it => ({ ...it, disabled: true })));
      history.goBack();
    }

    if (isSimulation) {
      const atualizarCalculo = updateCalculationObj?.contratos.filter(
        it => it.contrato !== contract.id_contrato,
      );
      const objeto = {
        ...updateCalculationObj,
        contratos: atualizarCalculo,
      };
      atualizarCalculoRequisicao(
        objeto as UpdateCalculation,
        contratos_filtrados,
      );
    }
  };

  const isStepDisabled = (
    contratosEditados: TerminationRequestsContractView[],
  ) => {
    const contratosPossuemEquipamentoSelecionadoEQtdeParcial =
      contratosEditados.every(contrato => {
        const contratosEquipamentosSelecionados = contrato.equipamentos.filter(
          eq => eq.selecionado,
        );

        return contratosEquipamentosSelecionados.length === 0
          ? false
          : contratosEquipamentosSelecionados.every(eq => eq.qtde_parcial > 0);
      });
    if (
      contratosPossuemEquipamentoSelecionadoEQtdeParcial ||
      isVisualization ||
      isAnalysis
    ) {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 2) {
            return { ...it, disabled: false };
          }
          return it;
        }),
      );
    } else {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 2) {
            return { ...it, disabled: true };
          }
          return it;
        }),
      );
    }
  };

  const editarQuantidadeParcial = (
    valor: number,
    contrato: TerminationRequestsContractView,
    equipamento: TerminationRequestsEquipament,
  ) => {
    const contratoEditado = contratos.map(it => {
      if (it.nu_contrato === contrato.nu_contrato) {
        const equipamentos = it.equipamentos.map(eq => {
          if (eq.id_equipamento === equipamento.id_equipamento) {
            return { ...eq, qtde_parcial: valor };
          }
          return eq;
        });
        return { ...it, equipamentos };
      }
      return it;
    });
    setContratos(contratoEditado);
    isStepDisabled(contratoEditado);
  };

  const selecionarEquipamento = (
    equipamentos: TerminationRequestsEquipament[],
    idContrato: number,
  ) => {
    const contratoEditado = contratos.map(it => {
      if (it.id_contrato === idContrato) {
        const equipamentosEditados = it.equipamentos.map(eq => {
          const eqSelecionado = equipamentos.find(
            eqs => eqs.id_equipamento === eq.id_equipamento,
          );
          if (eqSelecionado) {
            return { ...eq, selecionado: eqSelecionado.selecionado };
          }
          return eq;
        });
        return { ...it, equipamentos: equipamentosEditados };
      }
      return it;
    });
    setContratos(contratoEditado);
    isStepDisabled(contratoEditado);
  };

  const setarObjetoProposta = (m?: string, d?: string) => {
    setMotivo(m || '');
    setDescricao(d || '');
    const setarQtdeParcialContratos = contratos
      .filter(contrato => contrato.equipamentos.some(eq => eq.selecionado))
      .map(contrato => {
        const equipamentosAtualizados = contrato.equipamentos
          .filter(eq => eq.selecionado)
          .map(eq => ({
            ...eq,
            qtde_parcial: eq.qtde_parcial ?? eq.qtde_total,
          }));

        return { ...contrato, equipamentos: equipamentosAtualizados };
      });

    setObjProposta({
      contratos: setarQtdeParcialContratos,
      motivo: m || '',
      descricao_motivo: d || '',
    });
    if ((m && d) || isVisualization || isAnalysis) {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 3) {
            return { ...it, disabled: false };
          }
          return it;
        }),
      );
    } else {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 3) {
            return { ...it, disabled: true };
          }
          return it;
        }),
      );
    }
  };

  const cancelarSolicitacao = (ignorarModal = false) => {
    if (!ignorarModal) {
      onOpenModalCancel();
      return;
    }
    postCancelProposal(terminationId, motivoCancelamento)
      .then(() => {
        toast({
          title: 'Solicitação cancelada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      })
      .catch(err => {
        toast({
          title: 'Erro ao cancelar solicitação',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const atualizarCalculo = (
    valor: number,
    field: string,
    idContrato?: number,
  ) => {
    const obj = !idContrato
      ? ({
          ...updateCalculationObj,
          [field]: valor,
        } as UpdateCalculation)
      : ({
          ...updateCalculationObj,
          contratos: updateCalculationObj?.contratos.map(it => {
            if (it.contrato === idContrato) {
              return { ...it, [field]: valor };
            }
            return it;
          }),
        } as UpdateCalculation);
    setUpdateCalculationObj(obj);
    atualizarCalculoRequisicao(obj);
  };

  const atualizarCalculoRequisicao = (
    obj: UpdateCalculation,
    contratos_filtrados?: TerminationRequestsContractView[],
  ) => {
    postUpdateCalculation(
      isSimulation ? Number(idTerminationSimulation) : Number(terminationId),
      obj,
    ).then(res => {
      const ctts =
        isSimulation && contratos_filtrados ? contratos_filtrados : contratos;
      const atualizaContratos = ctts!.map(it => {
        const contrato = res.contratos?.find(
          c => c.contrato === it.id_contrato,
        );
        if (contrato) {
          return {
            ...it,
            valor_total: formatCurrency(contrato.valor_total || 0),
            valor_multa: formatCurrency(contrato.valor_multa || 0),
            aviso_previo: contrato.aviso_previo,
            multa: contrato.multa,
            valor_mensal_atual: formatCurrency(
              contrato.valor_mensal_atual || 0,
            ).replace('R$', ''),
          };
        }
        return it;
      });
      setContratos(atualizaContratos);
      setUpdateCalculationObj(res);
      verificaContratosInvalidos(atualizaContratos);
    });
  };

  const verificaContratosInvalidos = (c: TerminationRequestsContractView[]) => {
    const atributosParaVerificar: (keyof TerminationRequestsContractView)[] = [
      'multa',
      'aviso_previo',
      'valor_mensal_atual',
    ];

    const hasEmptyFields = c.some(contrato =>
      atributosParaVerificar.some(atributo => {
        return (
          contrato[atributo] === 0 ||
          contrato[atributo] == null ||
          contrato[atributo] === '' ||
          formataNumberStrToFloat(contrato[atributo].toString()) === 0
        );
      }),
    );

    if (!hasEmptyFields) {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 1) {
            return { ...it, disabled: false };
          }
          return it;
        }),
      );
    } else {
      setSteps(
        stepsControl.map(it => {
          if (it.id === 1) {
            return { ...it, disabled: true };
          }
          return it;
        }),
      );
    }
  };
  const downloadTermoRescisao = () => {
    setIsLoadingDownload(true);
    const request = isVisualizationAnalysis
      ? downloadTerminationPdf(Number(terminationId))
      : downloadTerminationDoc(
          isSimulation ? idTerminationSimulation : terminationId,
          updateCalculationObj!,
        );
    request
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        toast({
          title: `Erro`,
          description: 'Erro ao realizar o download do termo de rescisão',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };
  const handleFileChange = (_file: File | null) => {
    setSelectedFile(_file);
    setNomeArquivo(_file ? _file.name : '');
  };
  const clearFile = () => {
    setSelectedFile(null);
    setNomeArquivo('');
  };

  const canCancel = () => {
    if (isClient && solicitante === 0) {
      return undefined;
    }
    return isVisualization || isVisualizationAnalysis
      ? status !== 2 && status !== 3
        ? `${isClient ? 'Cancelar solicitação' : 'Cancelar proposta'}`
        : undefined
      : undefined;
  };

  return (
    <Box px="0" py="0">
      <Box px="8" py="4">
        <Heading hasBackButton>
          {!isAnalysis && !isVisualizationAnalysis
            ? isSimulation
              ? 'Simular rescisão de contrato'
              : 'Solicitar rescisão de contrato'
            : 'Proposta de rescisão de contrato'}
        </Heading>
      </Box>
      <HStack justifyContent="center">
        <Stepper
          steps={stepsControl}
          completed={() =>
            !isVisualization && !isVisualizationAnalysis
              ? solicitarProposta()
              : status === 2 && !isClient
              ? solicitarProposta(true)
              : history.goBack()
          }
          completedText={
            !isVisualization && !isVisualizationAnalysis
              ? !isAnalysis && !isSimulation
                ? 'Solicitar proposta'
                : 'Enviar proposta'
              : status === 2 && !isClient
              ? 'Proposta assinada'
              : 'Fechar'
          }
          selecionado={step => setStepSelecionado(step)}
          isLoading={loading}
          thirdButtonText={canCancel()}
          thirdButtonAction={() => {
            if (isVisualization || isVisualizationAnalysis) {
              cancelarSolicitacao();
            }
          }}
        >
          <Stack p={4} w="100%" alignItems="center" gridGap={2}>
            {stepSelecionado.id === 1 && (
              <>
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <Box
                      borderColor="transparent"
                      borderWidth="0"
                      borderRadius={8}
                      width="65%"
                      borderTopWidth="0"
                      borderTopColor="transparent"
                    >
                      {(isAnalysis || isSimulation) && (
                        <Alert
                          bg="#f7f2fa"
                          borderRadius="md"
                          color="#9246ce"
                          fontSize="sm"
                          marginTop="10px"
                          marginBottom="10px"
                          py={1}
                          px={2}
                        >
                          <AlertTitle>Atenção:</AlertTitle>
                          <AlertDescription>
                            <Text color="black">
                              Caso saia sem enviar a rescisão ao cliente, os
                              dados informados serão descartados.
                            </Text>
                          </AlertDescription>
                        </Alert>
                      )}
                      {(isAnalysis ||
                        isVisualizationAnalysis ||
                        isSimulation) && (
                        <HStack>
                          <Text fontSize="18px" fontWeight="bold">
                            Grupo Econômico:
                          </Text>
                          <Text fontWeight="bold">{grupoeconomico}</Text>
                        </HStack>
                      )}
                    </Box>
                    {contratos && contratos.length > 0 ? (
                      contratos.map(it => (
                        <ContractCard
                          key={it.nu_contrato}
                          contract={it}
                          removeContract={contract => removerContrato(contract)}
                          visualizacao={
                            isVisualization || isVisualizationAnalysis
                          }
                          visualizacaoAnalysis={isVisualizationAnalysis}
                          changeContractValue={(value, field, idContrato) => {
                            atualizarCalculo(value, field, idContrato);
                          }}
                          analisando={isAnalysis || isSimulation}
                          simulacao={isSimulation}
                        />
                      ))
                    ) : (
                      <Text>Nenhum contrato disponível</Text>
                    )}
                    {contratos &&
                      contratos.length > 0 &&
                      (isAnalysis ||
                        isSimulation ||
                        isVisualizationAnalysis) && (
                        <TotalValuesContract
                          data={updateCalculationObj}
                          visualizacao={isVisualizationAnalysis}
                          changeContractValue={(value, field) => {
                            atualizarCalculo(value, field);
                          }}
                        />
                      )}
                  </>
                )}
              </>
            )}
            {stepSelecionado.id === 2 && !isSimulation && (
              <>
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    {contratos && contratos.length > 0 ? (
                      contratos.map(it => (
                        <SelectEquipmentCard
                          key={it.nu_contrato}
                          contract={it}
                          quantidadeParcial={(qtde, contrato, equipamento) => {
                            editarQuantidadeParcial(
                              qtde,
                              contrato,
                              equipamento,
                            );
                          }}
                          selecionar={equipamentos => {
                            selecionarEquipamento(equipamentos, it.id_contrato);
                          }}
                          visualizacao={
                            isVisualization || isVisualizationAnalysis
                          }
                          analisando={isAnalysis}
                        />
                      ))
                    ) : (
                      <Text>Nenhum contrato disponível</Text>
                    )}
                  </>
                )}
              </>
            )}
            {stepSelecionado.id === 3 && (
              <RequestProposal
                motivo={motivo}
                descricao={descricao}
                visualizacao={isVisualization || isVisualizationAnalysis}
                analisando={isAnalysis}
                editarProposta={(m, d) => setarObjetoProposta(m, d)}
              />
            )}
            {stepSelecionado.id === 4 && (
              <Box
                borderColor="transparent"
                borderWidth="0"
                borderRadius={8}
                width="50%"
                borderTopWidth="0"
                borderTopColor="transparent"
              >
                {!isVisualizationAnalysis && (
                  <Alert
                    bg="#f7f2fa"
                    borderRadius="md"
                    color="#9246ce"
                    fontSize="sm"
                    marginTop="10px"
                    marginBottom="10px"
                    py={1}
                    px={2}
                  >
                    <AlertTitle>Atenção:</AlertTitle>
                    <AlertDescription>
                      <Text color="black">
                        Caso saia sem enviar a rescisão ao cliente, os dados
                        informados serão descartados.
                      </Text>
                    </AlertDescription>
                  </Alert>
                )}
                {!isVisualizationAnalysis && (
                  <HStack>
                    <Text fontSize="18px" fontWeight="bold">
                      Revise o Termo de Rescisão ou anexe a sua versão:
                    </Text>
                  </HStack>
                )}
                {isVisualizationAnalysis && (
                  <HStack>
                    <Text fontSize="18px" fontWeight="bold">
                      Visualize o Termo de Rescisão:
                    </Text>
                  </HStack>
                )}
                {status !== 2 && status !== 3 && (
                  <HStack>
                    <Text fontSize="14px">
                      Este termo será enviado ao cliente via DocuSign.
                    </Text>
                  </HStack>
                )}
                <HStack>
                  <Button
                    disabled={isLoadingDownload}
                    onClick={() => downloadTermoRescisao()}
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    style={{ marginTop: '20px', marginBottom: '50px' }}
                  >
                    {!isVisualizationAnalysis
                      ? 'Baixar Termo de Rescisão.doc'
                      : 'Baixar Termo de Rescisão'}
                    {isLoadingDownload && (
                      <Spinner style={{ marginLeft: '10px' }} />
                    )}
                  </Button>
                </HStack>
                {!isVisualizationAnalysis && (
                  <HStack>
                    <Text fontSize="18px" fontWeight="bold">
                      Anexar Termo de Rescisão:
                    </Text>
                    <Tooltip
                      hasArrow
                      label="Anexar arquivo do tipo pdf. O limite total do arquivo é de 10MB."
                      bg="black"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </HStack>
                )}
                {!isVisualizationAnalysis && (
                  <HStack>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} />
                      </InputLeftElement>
                      {(arquivo || nomeArquivo) && (
                        <InputRightElement pointerEvents="auto">
                          <Icon
                            as={FiX}
                            style={{
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                            }}
                            onClick={clearFile}
                          />
                        </InputRightElement>
                      )}
                      <input
                        id="file"
                        type="file"
                        onChange={e => {
                          handleFileChange(
                            e.target.files ? e.target.files[0] : null,
                          );
                          e.target.value = '';
                        }}
                        accept=".pdf"
                        ref={inputRef}
                        style={{ display: 'none' }}
                      />
                      <Input
                        placeholder="Anexar arquivo"
                        onClick={() =>
                          inputRef.current ? inputRef.current.click() : ''
                        }
                        value={nomeArquivo || ''}
                        name="arquivo"
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        isReadOnly
                      />
                    </InputGroup>
                    <Button
                      disabled={isLoadingDownload}
                      onClick={() =>
                        inputRef.current ? inputRef.current.click() : ''
                      }
                      color="black"
                      background="transparent"
                      border="1px solid black"
                      style={{ paddingLeft: '20px', paddingRight: '20px' }}
                    >
                      Anexar
                    </Button>
                  </HStack>
                )}
                {status === 2 && !isClient && (
                  <HStack>
                    <Text fontSize="18px" fontWeight="bold">
                      Termo de Rescisão assinado pelo cliente:
                    </Text>
                    <Tooltip
                      hasArrow
                      label="Anexar arquivo do tipo pdf. O limite total do arquivo é de 10MB"
                      bg="black"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </HStack>
                )}
                {status === 2 && !isClient && (
                  <HStack>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} />
                      </InputLeftElement>
                      {(arquivo || nomeArquivo) && (
                        <InputRightElement pointerEvents="auto">
                          <Icon
                            as={FiX}
                            style={{
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                            }}
                            onClick={clearFile}
                          />
                        </InputRightElement>
                      )}
                      <input
                        id="file"
                        type="file"
                        onChange={e => {
                          handleFileChange(
                            e.target.files ? e.target.files[0] : null,
                          );
                          e.target.value = '';
                        }}
                        accept=".pdf"
                        ref={inputRef}
                        style={{ display: 'none' }}
                      />
                      <Input
                        placeholder="Anexar arquivo"
                        onClick={() =>
                          inputRef.current ? inputRef.current.click() : ''
                        }
                        value={nomeArquivo || ''}
                        name="arquivo"
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        isReadOnly
                      />
                    </InputGroup>
                    <Button
                      disabled={isLoadingDownload}
                      onClick={() =>
                        inputRef.current ? inputRef.current.click() : ''
                      }
                      color="black"
                      background="transparent"
                      border="1px solid black"
                      style={{ paddingLeft: '20px', paddingRight: '20px' }}
                    >
                      Anexar
                    </Button>
                  </HStack>
                )}
              </Box>
            )}
            {stepSelecionado.id === 2 && isSimulation && (
              <TerminationAgreement
                baixarTermoRescisao={downloadTermoRescisao}
                arquivoSubstituido={handleFileChange}
              />
            )}
          </Stack>
        </Stepper>
      </HStack>
      <ConfirmationDialog
        confirmar={() => solicitarProposta(true)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo={
          isAnalysis || isSimulation
            ? 'Enviar a proposta de rescisão'
            : 'Solicitar proposta'
        }
        btnConfirmText={
          isAnalysis || isSimulation ? 'Confirmar' : 'Sim, solicitar'
        }
        isDisabled={loading}
        colorfulBar={false}
        loading={loading}
      >
        {!isAnalysis && !isSimulation ? (
          <span>
            Ao confirmar, a <b>Solicitação</b> será enviada. Deseja continuar?
          </span>
        ) : (
          <span>
            Ao confirmar, enviaremos um e-mail com os valores de rescisão
            detalhados para análise e validação do cliente.
          </span>
        )}
      </ConfirmationDialog>
      <ConfirmationDialog
        titulo="Erro ao buscar detalhe do contrato"
        onClose={() => {
          history.goBack();
          onClose();
        }}
        isOpen={isOpen}
        confirmar={() => {
          history.goBack();
          onClose();
        }}
        btnConfirmText="Fechar"
        hasCloseButton={false}
        colorfulBar={false}
      >
        <Text>{erro}</Text>
      </ConfirmationDialog>
      <ConfirmationDialog
        titulo="Cancelar solicitação"
        onClose={() => {
          onCloseModalCancel();
        }}
        isOpen={isOpenModalCancel}
        confirmar={() => {
          cancelarSolicitacao(true);
        }}
        btnConfirmText="Confirmar"
        colorfulBar={false}
        isDisabled={!motivoCancelamento || motivoCancelamento.length > 255}
      >
        <Stack gridGap={5}>
          <Text>
            Tem certeza que deseja cancelar a solicitação de rescisão de
            contrato?
          </Text>
          <FormControl isRequired>
            <FormLabel fontSize="14px">Informe o motivo</FormLabel>
            <Textarea
              name="motivo"
              error={errors?.motivo}
              value={motivoCancelamento}
              onChange={e => {
                if (e.target.value.length > 255) {
                  return;
                }
                setMotivoCancelamento(e.target.value);
              }}
              fontSize="15px"
              isRequired
            />
          </FormControl>
        </Stack>
      </ConfirmationDialog>
    </Box>
  );
};
